import { z } from "zod";
import { getEnv } from "./envManager";
import { safeFetch, safeFetchJSON } from "./safe-fetch";

const ADDIN_API_KEY_STORAGE = "DIOPTRA_ADDIN_API_KEY";
const ADDIN_API_KEY_LAST_REFRESH_STORAGE = "DIOPTRA_ADDIN_API_KEY_LAST_REFRESH";
const ADDIN_API_KEY_EXPIRES_AT_STORAGE = "DIOPTRA_ADDIN_API_KEY_EXPIRES_AT";
const LEGACY_ADDIN_USER_DISPLAY_NAME_STORAGE = "DIOPTRA_USER_DISPLAY_NAME";

function requireWebappBaseUrl(): string {
  const url = getEnv("WEBAPP_API_BASE_URL");
  if (!url) throw new Error("WEBAPP_API_BASE_URL is not configured");
  return url;
}

export function getAddinLoginUrl(code?: string): string {
  const base = `${requireWebappBaseUrl()}/auth/addin`;
  return code ? `${base}?code=${encodeURIComponent(code)}` : base;
}

const loginCodeSchema = z.object({ code: z.string().min(1), clientSecret: z.string().min(1) });

export type LoginCode = z.infer<typeof loginCodeSchema>;

export async function requestLoginCode(): Promise<LoginCode> {
  const data = await safeFetchJSON(`${requireWebappBaseUrl()}/api/auth/addin-code/request`, {
    method: "POST",
  });
  return loginCodeSchema.parse(data);
}

const loginKeyClaimSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("ready"), apiKey: z.string().min(1) }),
  z.object({ status: z.literal("pending") }),
  z.object({ status: z.literal("expired") }),
]);

export type LoginKeyClaim = z.infer<typeof loginKeyClaimSchema>;

export async function claimLoginKey(code: string, clientSecret: string): Promise<LoginKeyClaim> {
  const data = await safeFetchJSON(`${requireWebappBaseUrl()}/api/auth/addin-code/claim`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code, clientSecret }),
  });
  return loginKeyClaimSchema.parse(data);
}

export function getApiKey(): string | null {
  try {
    return localStorage.getItem(ADDIN_API_KEY_STORAGE);
  } catch {
    return null;
  }
}

export function setApiKey(key: string): void {
  localStorage.setItem(ADDIN_API_KEY_STORAGE, key);
  localStorage.removeItem(ADDIN_API_KEY_EXPIRES_AT_STORAGE);
  localStorage.removeItem(ADDIN_API_KEY_LAST_REFRESH_STORAGE);
}

export function clearApiKey(): void {
  localStorage.removeItem(ADDIN_API_KEY_STORAGE);
  localStorage.removeItem(ADDIN_API_KEY_LAST_REFRESH_STORAGE);
  localStorage.removeItem(ADDIN_API_KEY_EXPIRES_AT_STORAGE);
  localStorage.removeItem(LEGACY_ADDIN_USER_DISPLAY_NAME_STORAGE);
}

export function getApiKeyExpiresAt(): number | null {
  const raw = localStorage.getItem(ADDIN_API_KEY_EXPIRES_AT_STORAGE);
  const n = raw ? parseInt(raw, 10) : NaN;
  return Number.isFinite(n) ? n : null;
}

function setApiKeyExpiresAt(seconds: number): void {
  localStorage.setItem(ADDIN_API_KEY_EXPIRES_AT_STORAGE, String(seconds));
}

export function isApiKeyExpiredLocally(): boolean {
  const expiresAtSec = getApiKeyExpiresAt();
  if (expiresAtSec === null) return false;
  return Date.now() / 1000 > expiresAtSec;
}

function getLastRefreshAt(): number {
  const raw = localStorage.getItem(ADDIN_API_KEY_LAST_REFRESH_STORAGE);
  const n = raw ? parseInt(raw, 10) : NaN;
  return Number.isFinite(n) ? n : 0;
}

function setLastRefreshAt(ts: number): void {
  localStorage.setItem(ADDIN_API_KEY_LAST_REFRESH_STORAGE, String(ts));
}

export type RefreshResult =
  | { ok: true; expiresAt: number }
  | { ok: false; status: number };

interface RefreshApiKeyOptions {
  force: boolean;
  debounceMs: number;
}

export async function refreshApiKeyOnServer({ force, debounceMs }: RefreshApiKeyOptions): Promise<RefreshResult | null> {
  const key = getApiKey();
  if (!key) return null;

  const sinceLast = Date.now() - getLastRefreshAt();
  if (!force && sinceLast < debounceMs) {
    console.log(`[dioptra-auth] refreshApiKeyOnServer: skipped (last refresh ${Math.round(sinceLast / 1000)}s ago, debounce ${debounceMs / 1000}s)`);
    return null;
  }

  const webappBaseUrl = getEnv("WEBAPP_API_BASE_URL");
  if (!webappBaseUrl) return null;

  console.log(`[dioptra-auth] refreshApiKeyOnServer: POST ${webappBaseUrl}/api/auth/addin-code/refresh (force=${force})`);
  let response: Response;
  try {
    response = await safeFetch(`${webappBaseUrl}/api/auth/addin-code/refresh`, {
      method: "POST",
      headers: { Authorization: `Bearer ${key}` },
    });
  } catch (err) {
    const status = (err as { status?: number })?.status;
    if (typeof status === "number") {
      console.warn(`[dioptra-auth] refreshApiKeyOnServer: HTTP ${status}`);
      return { ok: false, status };
    }
    console.warn("[dioptra-auth] refreshApiKeyOnServer: network error", err);
    return null;
  }

  setLastRefreshAt(Date.now());
  let expiresAt = 0;
  try {
    const data = await response.json();
    expiresAt = typeof data?.expiresAt === "number" ? data.expiresAt : 0;
  } catch {
    // body shape changed — extension still succeeded.
  }
  if (expiresAt) {
    setApiKeyExpiresAt(expiresAt);
    const human = new Date(expiresAt * 1000).toISOString();
    console.log(`[dioptra-auth] refreshApiKeyOnServer: OK, new expiresAt=${expiresAt} (${human})`);
  } else {
    console.log("[dioptra-auth] refreshApiKeyOnServer: OK (no expiresAt in body)");
  }
  return { ok: true, expiresAt };
}
